import { TrainingModuleDto, TrainingTopicDto, TrainingContentDto } from './index';

// Represents a content item organized by its module and topic for table display
export interface TrainingTableContentItem {
  contentId: string;
  contentName: string;
  contentSequence: number;
  topicId: string;
  topicName: string;
  topicSequence: number;
  moduleId: string;
  moduleName: string;
  moduleSequence: number;
}

// Represents the progress state for a specific org-content cell
export interface TrainingTableCellProgress {
  progressId?: string; // Undefined if no progress record exists yet
  isCompleted: boolean;
  score?: number;
}

// Represents a row in the training progress table (one organization's progress)
export interface TrainingTableRow {
  enrollmentId: string;
  organizationId: string;
  organizationName: string;
  groupId: string | null;
  groupName: string | null;
  adminUserId: string | null;
  adminUserIdNumber: string | null;
  adminUserName: string | null;
  regionName: string | null;
  adminUserPhones: string[];
  progressPercentage: number;
  completedAt: string | null;
  // Map of contentId -> progress state
  contentProgress: Record<string, TrainingTableCellProgress>;
}

// Organized content hierarchy for column generation
export interface TrainingTableModuleGroup {
  moduleId: string;
  moduleName: string;
  moduleSequence: number;
  topics: TrainingTableTopicGroup[];
}

export interface TrainingTableTopicGroup {
  topicId: string;
  topicName: string;
  topicSequence: number;
  contents: TrainingTableContentItem[];
}

// Full training table data structure
export interface TrainingTableData {
  trainingId: string;
  trainingName: string;
  modules: TrainingTableModuleGroup[];
  rows: TrainingTableRow[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

// Helper to transform API response to table structure
export function transformToTableData(
  trainingId: string,
  trainingName: string,
  enrollmentProgressData: any, // PaginatedResponse<TrainingEnrollmentProgressDto>
  modules: TrainingModuleDto[],
  topics: TrainingTopicDto[],
  contents: TrainingContentDto[]
): TrainingTableData {
  // Build content hierarchy
  const moduleGroups: TrainingTableModuleGroup[] = modules
    .sort((a, b) => a.sequence - b.sequence)
    .map(module => {
      const moduleTopics = topics
        .filter(t => t.moduleId === module.id)
        .sort((a, b) => a.sequence - b.sequence)
        .map(topic => {
          const topicContents = contents
            .filter(c => c.topicId === topic.id)
            .sort((a, b) => a.sequence - b.sequence)
            .map(content => ({
              contentId: content.id,
              contentName: content.name,
              contentSequence: content.sequence,
              topicId: topic.id,
              topicName: topic.name,
              topicSequence: topic.sequence,
              moduleId: module.id,
              moduleName: module.name,
              moduleSequence: module.sequence,
            }));
          
          return {
            topicId: topic.id,
            topicName: topic.name,
            topicSequence: topic.sequence,
            contents: topicContents,
          };
        });
      
      return {
        moduleId: module.id,
        moduleName: module.name,
        moduleSequence: module.sequence,
        topics: moduleTopics,
      };
    });

  // Transform enrollment progress to table rows
  const rows: TrainingTableRow[] = enrollmentProgressData.content.map((enrollment: any) => {
    const contentProgressMap: Record<string, TrainingTableCellProgress> = {};
    
    // Populate existing progress
    enrollment.contentProgress.forEach((cp: any) => {
      contentProgressMap[cp.contentId] = {
        progressId: cp.progressId,
        isCompleted: cp.isCompleted,
        score: undefined, // Can be added if needed
      };
    });
    
    // For any content without progress, mark as incomplete
    contents.forEach(content => {
      if (!contentProgressMap[content.id]) {
        contentProgressMap[content.id] = {
          progressId: undefined,
          isCompleted: false,
        };
      }
    });
    
    return {
      enrollmentId: enrollment.enrollmentId,
      organizationId: enrollment.organizationId,
      organizationName: enrollment.organizationName,
      groupId: enrollment.groupId,
      groupName: enrollment.groupName,
      adminUserId: enrollment.adminUserId,
      adminUserIdNumber: enrollment.adminUserIdNumber,
      adminUserName: enrollment.adminUserName,
      regionName: enrollment.regionName,
      adminUserPhones: enrollment.adminUserPhones || [],
      progressPercentage: enrollment.progressPercentage,
      completedAt: enrollment.completedAt,
      contentProgress: contentProgressMap,
    };
  });

  return {
    trainingId,
    trainingName,
    modules: moduleGroups,
    rows,
    totalElements: enrollmentProgressData.totalElements,
    totalPages: enrollmentProgressData.totalPages,
    currentPage: enrollmentProgressData.page,
    pageSize: enrollmentProgressData.size,
  };
}

